import React from "react";
import { motion } from "framer-motion";
import { X, Star, MapPin, Thermometer, Users, Coffee, Waves } from "lucide-react";

const getAmenityIcon = (amenity) => {
  const iconMap = {
    "Spa services": Waves,
    Restaurant: Coffee,
    Lodge: Users,
    "Full spa": Waves,
    "Fitness center": Users,
    Dining: Coffee,
    Accommodation: Users,
    "Changing rooms": Users,
    "Picnic areas": Coffee,
    "Pool complex": Waves,
    "Event space": Users,
  };

  const IconComponent = iconMap[amenity] || Users;
  return <IconComponent className="w-4 h-4" />;
};

const ResultsPopup = ({ springs, onClose }) => {
  return (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        exit={{ y: 20 }}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="p-6 lg:p-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-6">
            Matching Hot Springs ({springs.length})
          </h2>

          <div className="space-y-8">
            {springs.map((spring) => (
              <div key={spring.id} className="border-b border-gray-100 pb-8 last:border-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Image */}
                  <div className="relative h-48 rounded-xl overflow-hidden">
                    <div className="absolute inset-0">
                      <img 
                        src={spring.image} 
                        alt={spring.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-spa-blue-400/20 to-spa-teal-400/20 flex items-center justify-center">
                        <div className="text-white/60 text-6xl font-serif">
                          {spring.name.charAt(0)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-serif font-bold text-gray-800">
                        {spring.name}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mt-2">
                        <div className="flex items-center space-x-1">
                          <MapPin className="w-4 h-4" />
                          <span>{spring.location}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Thermometer className="w-4 h-4" />
                          <span>{spring.temperature}</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm">{spring.description}</p>

                    {/* Features */}
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm mb-2">
                        Features
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {spring.features.map((feature, i) => (
                          <div key={i} className="flex items-center space-x-2 text-xs text-gray-600">
                            <div className="w-1.5 h-1.5 bg-spa-blue-500 rounded-full"></div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Amenities */}
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm mb-2">
                        Amenities
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {spring.amenities.slice(0, 4).map((amenity, i) => (
                          <div key={i} className="flex items-center space-x-2 px-2 py-1 bg-spa-blue-50 text-spa-blue-700 rounded-md text-xs">
                            {getAmenityIcon(amenity)}
                            <span>{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ResultsPopup;
